import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  X,
  Send,
  Sparkles,
  Loader2,
  ShieldCheck,
  RotateCcw,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const SESSION_KEY = "gh_chat_session";
const HISTORY_KEY = "gh_chat_history";

const SUGGESTIONS = [
  "Can you remove my Google reviews?",
  "How much does it cost?",
  "What kinds of reviews qualify?",
  "How long does removal take?",
];

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveHistory(msgs) {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(msgs.slice(-40)));
  } catch {}
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => loadHistory());
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);

  const sessionId = useMemo(() => {
    if (typeof window === "undefined") return null;
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  }, []);

  useEffect(() => {
    saveHistory(messages);
  }, [messages]);

  useEffect(() => {
    if (open) {
      setUnread(false);
      requestAnimationFrame(() => {
        listRef.current?.scrollTo({ top: 1e9, behavior: "smooth" });
        inputRef.current?.focus();
      });
    }
  }, [open, messages.length]);

  // Nudge new visitors — one-time animation on the bubble
  const [pulseNudge, setPulseNudge] = useState(false);
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => setPulseNudge(true), 6000);
    return () => clearTimeout(t);
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;

    const userMsg = { role: "user", content: msg, at: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);

    try {
      const { data } = await api.post("/chat", {
        session_id: sessionId,
        message: msg,
      });
      const botMsg = {
        role: "assistant",
        content: data.reply,
        at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, botMsg]);
      if (!open) setUnread(true);
    } catch (err) {
      const detail = formatApiErrorDetail(err?.response?.data?.detail);
      const fallback =
        detail ||
        "Ella can't reach the network right now. Please try again in a moment — or use the free audit form / book a call while I recover.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: fallback, at: new Date().toISOString(), error: true },
      ]);
    } finally {
      setSending(false);
    }
  };

  const resetChat = () => {
    setMessages([]);
    saveHistory([]);
    // Keep session_id — history on server is scoped to it but the widget shows empty
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close chat" : "Open chat with Ella"}
        data-testid="chat-launcher"
        className="fixed z-[70] bottom-5 right-5 sm:bottom-6 sm:right-6 group"
      >
        <motion.div
          animate={pulseNudge && !open ? { scale: [1, 1.06, 1] } : { scale: 1 }}
          transition={pulseNudge && !open ? { duration: 1.8, repeat: 3 } : {}}
          className="relative"
        >
          {/* Glow ring */}
          <span
            aria-hidden
            className="absolute -inset-2 rounded-full bg-[#10B981]/25 blur-xl opacity-70 group-hover:opacity-100 transition-opacity"
          />
          <span className="relative w-14 h-14 grid place-items-center rounded-full bg-gradient-to-br from-[#10B981] to-[#0e8f66] text-white shadow-2xl shadow-emerald-500/40 ring-1 ring-white/10">
            <AnimatePresence mode="wait" initial={false}>
              {open ? (
                <motion.span
                  key="x"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <X className="w-6 h-6" />
                </motion.span>
              ) : (
                <motion.span
                  key="chat"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="relative"
                >
                  <MessageCircle className="w-6 h-6" />
                  {unread && (
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[#D4AF37] shadow-[0_0_8px_#D4AF37]" />
                  )}
                </motion.span>
              )}
            </AnimatePresence>
          </span>
        </motion.div>
      </button>

      {/* Chat panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[70] bottom-24 right-4 sm:right-6 w-[min(94vw,380px)] max-h-[calc(100vh-8rem)] flex flex-col rounded-3xl border border-white/10 bg-[#0d1424]/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden"
            data-testid="chat-panel"
          >
            {/* Header */}
            <div className="relative px-5 py-4 border-b border-white/10 bg-gradient-to-r from-[#10B981]/[0.10] via-white/[0.02] to-transparent">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-10 h-10 grid place-items-center rounded-full bg-gradient-to-br from-[#10B981] to-[#0e8f66] font-display font-bold text-white shadow-lg shadow-emerald-500/30">
                    E
                  </div>
                  <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-[#10B981] ring-2 ring-[#0d1424]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-white text-sm">
                    Ella <span className="text-white/50 font-normal">· Go Higher AI</span>
                  </div>
                  <div className="text-[11px] text-white/50 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" />
                    Online · replies instantly
                  </div>
                </div>
                {messages.length > 0 && (
                  <button
                    onClick={resetChat}
                    aria-label="Reset conversation"
                    className="w-8 h-8 grid place-items-center rounded-full border border-white/10 text-white/50 hover:text-white hover:border-white/25 transition-colors"
                    data-testid="chat-reset"
                  >
                    <RotateCcw className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
              data-testid="chat-messages"
            >
              {messages.length === 0 && (
                <div className="text-center py-4 space-y-3">
                  <div className="mx-auto w-12 h-12 grid place-items-center rounded-2xl bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981]">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div className="text-white font-display font-bold text-base">
                    Hi, I'm Ella 👋
                  </div>
                  <div className="text-white/60 text-xs leading-relaxed max-w-[280px] mx-auto">
                    Ask me anything about removing damaging Google reviews,
                    pricing, or how our free audit works.
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <ChatBubble key={i} m={m} />
              ))}
              {sending && (
                <div className="flex items-end gap-2 pr-8">
                  <BotAvatar />
                  <div className="rounded-2xl bg-white/[0.05] border border-white/10 px-4 py-3 flex items-center gap-1.5">
                    <Dot delay={0} />
                    <Dot delay={0.15} />
                    <Dot delay={0.3} />
                  </div>
                </div>
              )}
            </div>

            {/* Suggestions (only shown when empty) */}
            {messages.length === 0 && (
              <div className="px-4 pb-3 flex flex-wrap gap-1.5">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="text-[11px] px-2.5 py-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:border-[#10B981]/40 hover:text-[#10B981] hover:bg-[#10B981]/[0.06] text-white/70 transition-colors"
                    data-testid="chat-suggestion"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/10 p-3 flex items-end gap-2"
            >
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Ask Ella a question…"
                  disabled={sending}
                  data-testid="chat-input"
                  className="w-full resize-none bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 pr-10 text-sm text-white placeholder:text-white/35 focus:border-[#10B981]/60 focus:bg-[#10B981]/[0.04] focus:ring-2 focus:ring-[#10B981]/15 focus:outline-none transition-colors max-h-32"
                  style={{ minHeight: 44 }}
                />
              </div>
              <button
                type="submit"
                disabled={!input.trim() || sending}
                data-testid="chat-send"
                aria-label="Send message"
                className="shrink-0 w-11 h-11 grid place-items-center rounded-2xl bg-[#10B981] hover:bg-[#0ea774] text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-lg shadow-emerald-500/25"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </form>

            {/* Footer trust line */}
            <div className="border-t border-white/5 px-4 py-2 flex items-center gap-1.5 text-[10px] text-white/40">
              <ShieldCheck className="w-3 h-3" />
              Powered by AI · specialist replies from your team
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function BotAvatar() {
  return (
    <div className="w-7 h-7 shrink-0 grid place-items-center rounded-full bg-gradient-to-br from-[#10B981] to-[#0e8f66] text-white text-xs font-display font-bold">
      E
    </div>
  );
}

function Dot({ delay }) {
  return (
    <motion.span
      className="w-1.5 h-1.5 rounded-full bg-white/60"
      animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
      transition={{ duration: 0.8, repeat: Infinity, delay }}
    />
  );
}

function ChatBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      className={`flex items-end gap-2 ${isUser ? "justify-end pl-8" : "pr-8"}`}
    >
      {!isUser && <BotAvatar />}
      <div
        className={`rounded-2xl px-4 py-2.5 text-sm leading-relaxed max-w-[85%] whitespace-pre-wrap ${
          isUser
            ? "bg-[#10B981] text-white rounded-br-sm shadow-lg shadow-emerald-500/20"
            : m.error
            ? "bg-[#D4AF37]/[0.10] border border-[#D4AF37]/30 text-white rounded-bl-sm"
            : "bg-white/[0.05] border border-white/10 text-white rounded-bl-sm"
        }`}
      >
        {m.content}
      </div>
    </motion.div>
  );
}
